import React from 'react'
import { Link } from '@fs/zion-router'
import { i18n } from '@fs/zion-locale'
import { css } from '@emotion/core'
import { encodeArk } from '@fs/zion-ark'
import transl from './Metadata.transl'
import { EditableField, ListItemGroupData, LanguagePicker, CheckboxListInputs } from '../../components'

const padLeft = css`
  padding-inline-start: 8px;
`
export const listFormat = new Intl.ListFormat(i18n.language, { style: 'narrow', type: 'conjunction' })

export const renderTitleField = ({ metadata, editable, errors, setTitle, generateCopyButton }) => (
  <EditableField
    editing={editable}
    value={metadata?.title}
    onDebounce={setTitle}
    label={transl.title()}
    error={errors?.includes('title')}
    ElseElement={
      <ListItemGroupData
        overline={transl.title()}
        items={[metadata?.title]}
        rightElement={generateCopyButton(metadata?.title, transl.title())}
        glossaryDescription={transl.glossary.title()}
      />
    }
  />
)

export const renderVolumeField = ({ metadata, editable, errors, setVolume, generateCopyButton }) => (
  <EditableField
    editing={editable}
    value={metadata?.volumes?.join('\n') ?? ''}
    onDebounce={setVolume}
    label={transl.volume()}
    error={errors?.includes('volume')}
    ElseElement={
      <ListItemGroupData
        overline={transl.volume()}
        items={listFormat.format(metadata?.volumes ?? [])}
        rightElement={generateCopyButton(metadata?.volumes?.length ?? 0, transl.volume())}
        glossaryDescription={transl.glossary.volume()}
      />
    }
    multiValue
    helpText={transl.multiValueHelpText()}
  />
)

export const renderLanguageField = ({
  editable,
  metadata,
  languagesData,
  setLanguages,
  errors,
  generateCopyButton,
  languageTextItems,
}) => {
  return editable ? (
    <LanguagePicker
      languageOptions={languagesData?.standardsLanguages || []}
      selectedCodes={metadata?.languages || []}
      label={transl.language()}
      onChange={setLanguages}
      error={errors?.includes('languages')}
    />
  ) : (
    <ListItemGroupData
      overline={transl.language()}
      items={languageTextItems}
      rightElement={
        Array.isArray(languageTextItems) &&
        languageTextItems.length > 0 &&
        generateCopyButton(listFormat.format(languageTextItems), transl.language())
      }
      glossaryDescription={transl.glossary.language()}
      commaSeparated
    />
  )
}

export const renderCreatorField = ({
  editable,
  metadata,
  deleteCoveragesMode,
  deleteMode,
  setCreator,
  handleCreatorChange,
  generateCopyButton,
}) =>
  editable ? (
    <CheckboxListInputs
      label={transl.creator()}
      selectAllLabel={transl.selectAllCreators()}
      disabled={deleteCoveragesMode || deleteMode}
      inputPlaceHolder={transl.creatorPlaceholder()}
      values={metadata?.creator}
      setValues={(getValues) => setCreator(getValues(metadata?.creator || {}))}
      onSelectionChange={handleCreatorChange}
    />
  ) : (
    <ListItemGroupData
      overline={transl.creator()}
      hyperlinks={Object.values(metadata?.creator || {})?.map((item) => getSearchResultsLink('creator', item))}
      rightElement={generateCopyButton(listFormat.format(Object.values(metadata?.creator ?? [])), transl.creator())}
      glossaryDescription={transl.glossary.creator()}
    />
  )

export const renderImageGroupNumber = ({ editable, metadata, itemCount, itemNumber, isDivideMode, dividerOffsets }) => {
  if (editable) return null

  const linkData = getSearchResultsLink('imageGroupNumbers', metadata?.imageGroupNumber)

  return (
    <ListItemGroupData
      overline={transl.imageGroupNumber()}
      primaryText={
        <span>
          {!!linkData?.to && (
            <Link target="_blank" external to={linkData.to}>
              {linkData.text}
            </Link>
          )}
          {itemCount > 1 && (
            <span css={padLeft}>
              {transl.itemNumberTextForGroupID(itemNumber, isDivideMode ? dividerOffsets?.length : itemCount)}
            </span>
          )}
        </span>
      }
      glossaryDescription={transl.glossary.imageGroupNumber()}
    />
  )
}

export const renderParentGroupLinks = ({ parentGroupsResponse, parentGroupsLoading }) => {
  if (parentGroupsLoading || !parentGroupsResponse?.groupParents?.length) return null

  const hyperlinks = parentGroupsResponse.groupParents
    .filter((parentGroup) => parentGroup.imageApids?.length)
    .map((parentGroup) => ({
      skipEncode: true,
      text: parentGroup.id,
      to: `/ark:/61903/${encodeArk(parentGroup.imageApids[0])}?view=explore&groupId=${parentGroup.id}`,
    }))

  return <ListItemGroupData hyperlinks={hyperlinks} overline={transl.parentGroups()} />
}

export function getSearchResultsLink(key, value) {
  if (!(key && value)) return null

  const appPath = '/records/images/search-results'

  return {
    text: value,
    to: `${appPath}?${key}=${value}`,
  }
}
